import { ChangeEvent, ReactElement, ReactNode, useCallback, useMemo } from 'react';
import {
  BodyCell,
  ClearFiltersButton,
  FilterCell,
  FilterInput,
  FilterRow,
  HeaderButton,
  HeaderCell,
  HeaderContent,
  HeaderRow,
  HeaderTitle,
  RetryButton,
  SortIndicator,
  Status,
  StyledTable,
  TableContainer,
  TableHeader,
  TableRoot,
  TableSummary,
  TableViewport,
} from './styles';
import { isTableFilterActive, TableFilterControl } from './TableFilterControl';
import {
  BaseServerTableProps,
  TableColumn,
  TableFilters,
  TableSort,
  TableSortDirection,
} from './types';

const getColumnWidths = <Row,>(columns: TableColumn<Row>[]) => {
  const totalPercentage = columns.reduce((total, { width }) => {
    if (typeof width !== 'string' || !width.endsWith('%')) return total;
    return total + Number.parseFloat(width);
  }, 0);

  return columns.map(({ width }) => {
    if (typeof width !== 'string' || !width.endsWith('%') || !totalPercentage) return width;
    return `${Number.parseFloat(width) / totalPercentage * 100}%`;
  });
};

export const buildColGroup = <Row,>(columns: TableColumn<Row>[]) => {
  const widths = getColumnWidths(columns);
  return (
    <colgroup>
      {columns.map((column, index) => (
        <col key={column.key} style={{ width: widths[index] }} />
      ))}
    </colgroup>
  );
};

type TableCoreProps<Row> = Pick<
  BaseServerTableProps<Row>,
  | 'columns'
  | 'showFilterSummary'
  | 'clearFiltersLabel'
  | 'emptyMessage'
  | 'filteredEmptyMessage'
  | 'errorMessage'
  | 'loadingMessage'
  | 'className'
  | 'minWidth'
> & {
  rowsLength: number;
  loading: boolean;
  error: unknown;
  sort?: TableSort;
  draftFilters: TableFilters;
  clearFilters: () => void;
  retryLoad: () => void;
  updateSort: (column: string, defaultDirection?: TableSortDirection) => void;
  updateFilter: (column: string, event: ChangeEvent<HTMLInputElement>) => void;
  updateTypedFilter: (column: string, value: TableFilters[string]) => void;
  renderBody: (renderRow: (index: number, row: Row) => ReactElement) => ReactNode;
  footer?: ReactNode;
};

const TableCore = <Row,>({
  columns,
  rowsLength,
  loading,
  error,
  sort,
  draftFilters,
  clearFilters,
  retryLoad,
  updateSort,
  updateFilter,
  updateTypedFilter,
  renderBody,
  footer,
  showFilterSummary = false,
  clearFiltersLabel = 'Clear all filters ×',
  emptyMessage = 'No items',
  filteredEmptyMessage,
  errorMessage = 'Could not load the data.',
  loadingMessage = 'Loading…',
  className,
  minWidth,
}: TableCoreProps<Row>): ReactElement => {
  const hasFilterColumns = useMemo(() => columns.some(({ filterable }) => filterable), [columns]);
  const activeFilterCount = useMemo(
    () => Object.values(draftFilters).filter(isTableFilterActive).length,
    [draftFilters],
  );

  const renderHeader = useCallback(() => (
    <>
      <HeaderRow>
        {columns.map((column) => {
          const sortField = column.sortField ?? column.key;
          const filterField = column.filterField ?? column.key;
          const isSorted = sort?.column === sortField;
          const textAlign = column.align === 'right' ? 'end' : column.align === 'center' ? 'center' : 'start';
          const justifyContent = column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start';
          return (
            <HeaderCell
              key={column.key}
              style={{ width: column.width, textAlign }}
              aria-sort={isSorted ? (sort.direction === 'ASC' ? 'ascending' : 'descending') : 'none'}
            >
              <HeaderContent style={{ justifyContent }}>
                {column.sortable ? (
                  <HeaderButton
                    type="button"
                    active={isSorted}
                    onClick={() => updateSort(sortField, column.defaultSortDirection)}
                    style={{ justifyContent }}
                    aria-label={`Sort by ${typeof column.title === 'string' ? column.title : column.key}`}
                  >
                    <HeaderTitle>{column.title}</HeaderTitle>
                    <SortIndicator active={isSorted} aria-hidden="true">
                      {isSorted ? (sort.direction === 'ASC' ? '▲' : '▼') : '⇅'}
                    </SortIndicator>
                  </HeaderButton>
                ) : (
                  <HeaderTitle>{column.title}</HeaderTitle>
                )}
                {column.filter && (
                  <TableFilterControl
                    columnTitle={typeof column.title === 'string' ? column.title : column.key}
                    filter={column.filter}
                    value={draftFilters[filterField]}
                    onChange={(value) => updateTypedFilter(filterField, value)}
                  />
                )}
              </HeaderContent>
            </HeaderCell>
          );
        })}
      </HeaderRow>
      {hasFilterColumns && (
        <FilterRow>
          {columns.map((column) => (
            <FilterCell
              key={column.key}
              style={{ textAlign: column.align === 'right' ? 'end' : column.align === 'center' ? 'center' : 'start' }}
            >
              {column.filterable && (
                <FilterInput
                  type="search"
                  value={typeof draftFilters[column.key] === 'string' ? (draftFilters[column.key] as string) : ''}
                  onChange={(event) => updateFilter(column.key, event)}
                  placeholder={column.filterPlaceholder ?? 'Filter…'}
                  aria-label={`Filter ${typeof column.title === 'string' ? column.title : column.key}`}
                />
              )}
            </FilterCell>
          ))}
        </FilterRow>
      )}
    </>
  ), [columns, draftFilters, hasFilterColumns, sort, updateFilter, updateSort, updateTypedFilter]);

  const renderRow = useCallback((_index: number, row: Row) => (
    <>
      {columns.map((column) => (
        <BodyCell
          key={column.key}
          style={{ textAlign: column.align === 'right' ? 'end' : column.align === 'center' ? 'center' : 'start' }}
        >
          {column.render?.(row) ?? String((row as Record<string, unknown>)[column.key] ?? '')}
        </BodyCell>
      ))}
    </>
  ), [columns]);

  return (
    <TableRoot className={className}>
      {showFilterSummary && (
        <TableSummary>
          {activeFilterCount > 0 && (
            <ClearFiltersButton type="button" onClick={clearFilters}>
              {clearFiltersLabel}
            </ClearFiltersButton>
          )}
        </TableSummary>
      )}
      <TableContainer aria-busy={loading} style={{ minWidth }}>
        <TableHeader>
          <StyledTable>
            {buildColGroup(columns)}
            <thead>{renderHeader()}</thead>
          </StyledTable>
        </TableHeader>
        <TableViewport>
          {renderBody(renderRow)}
          {!rowsLength && !loading && !error && (
            <Status overlay>{activeFilterCount > 0 && filteredEmptyMessage ? filteredEmptyMessage : emptyMessage}</Status>
          )}
          {loading && (
            <Status overlay={!rowsLength} role="status">
              {loadingMessage}
            </Status>
          )}
          {Boolean(error) && (
            <Status overlay={!rowsLength} role="alert">
              {errorMessage}
              <RetryButton type="button" onClick={retryLoad}>Retry</RetryButton>
            </Status>
          )}
        </TableViewport>
      </TableContainer>
      {footer}
    </TableRoot>
  );
};

export default TableCore;